"use client";
import { useEffect, useState, useRef, useMemo } from "react";
import Banner from "@/components/Banner";
import { ICMS, IGallery } from "@/store/home/homeModel";
import { cleanHeaderTags, createLookupObject, decodeHtml } from "@/lib/helper";
import { OptimizedImage } from "@/components/common/OptimizedImage";
import dynamic from "next/dynamic";
import { ChevronLeftIcon, ChevronRightIcon, X } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  FreeMode,
  Navigation,
  Thumbs,
  Keyboard,
  Pagination,
} from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
import "swiper/css/pagination";

const Masonry = dynamic(() => import("react-responsive-masonry"), {
  ssr: false,
});

interface IGalleryItem {
  ID: number;
  GalleryImage: string;
  ImgAlt: string;
  GroupName: string;
}

interface IGalleryGroup {
  GroupName: string;
  Images: IGalleryItem[];
}

interface GalleryProps {
  cmsData: ICMS[];
  bannerData: any[];
  galleryData: IGalleryGroup[];
}

export default function Gallery({
  cmsData,
  bannerData,
  galleryData,
}: GalleryProps) {
  const [selectedGroup, setSelectedGroup] = useState<string>("All");
  const [isSticky, setIsSticky] = useState(false);
  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [columnsCount, setColumnsCount] = useState(3);
  const galleryRef = useRef<HTMLDivElement>(null);

  const allImages = galleryData.flatMap((group) => group.Images);
  const groups = ["All", ...galleryData.map((group) => group.GroupName)];

  const filteredImages =
    selectedGroup === "All"
      ? allImages
      : galleryData.find((group) => group.GroupName === selectedGroup)
          ?.Images || [];

  const handleGroupChange = (group: string) => {
    setSelectedGroup(group);
    if (galleryRef?.current) {
      const yOffset = -180; // Adjust this for sticky header/tabs height
      const y =
        galleryRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const openLightbox = (index: number): void => {
    setActiveIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = (): void => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setColumnsCount(1);
      } else if (window.innerWidth < 1024) {
        setColumnsCount(2);
      } else {
        setColumnsCount(3);
      }
    };
    handleResize();
    const handleScroll = () => {
      setIsSticky(window.scrollY > 400);
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape") closeLightbox();
    };
    if (lightboxOpen) {
      window.addEventListener("keydown", handleEscape);
    }
    return () => window.removeEventListener("keydown", handleEscape);
  }, [lightboxOpen]);

  const banner =
    bannerData?.filter((banner: any) => banner?.BannerType === "gallery") || [];

  return (
    <>
      <Banner BannerList={banner} />

      {/* Group Selector */}
      <div
        className={`sticky top-24 z-40 transition-all duration-300 ${
          isSticky ? "bg-white shadow-lg" : "bg-[#F3EBDD]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex gap-3 overflow-x-auto hide-scrollbar">
            {groups.map((group) => (
              <button
                key={group}
                onClick={() => handleGroupChange(group)}
                className={`px-6 py-3 rounded-full whitespace-nowrap font-medium transition-all ${
                  selectedGroup === group
                    ? "bg-[#7A4A1D] text-white shadow-lg"
                    : "bg-white text-[#7A4A1D] hover:bg-[#7A4A1D]/10 border border-[#E8DCC8]"
                }`}
              >
                {group}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section
        ref={galleryRef}
        className="py-12 bg-gradient-to-b from-white to-[#F3EBDD]/30"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Masonry columnsCount={columnsCount} gutter="1.5rem">
            {filteredImages.map((image: any, index: number) => (
              <div
                key={index}
                className="cursor-pointer group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all"
                onClick={() => openLightbox(index)}
              >
                <OptimizedImage
                  src={image.GalleryImage}
                  alt={image.ImgAlt || `Gallery image ${index + 1}`}
                  width={800}
                  height={600}
                  className="w-full h-auto group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                  <span className="text-white text-lg font-medium">
                    {image.ImgAlt}
                  </span>
                </div>
              </div>
            ))}
          </Masonry>
          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-[#7A4A1D]">
                No images found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {lookUpCMSData?.["Gallery - CTA"] && (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanHeaderTags(
              decodeHtml(lookUpCMSData?.["Gallery - CTA"]?.Template),
            ),
          }}
        />
      )}

      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/95 z-[100] flex flex-col"
          style={{ background: "#000000f2" }}
          onClick={(e) => e.target === e.currentTarget && closeLightbox()}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-2 right-1 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-50 transition-colors"
            aria-label="Close lightbox"
          >
            <X size={24} />
          </button>

          <div className="absolute top-5 left-1/2 -translate-x-1/2 bg-black/50 text-white px-5 py-2 rounded-full text-sm backdrop-blur-sm z-50">
            {activeIndex + 1} / {filteredImages?.length}
          </div>

          <div
            className="flex-1 mt-16 mb-4 relative"
            style={{ height: "calc(100vh - 190px)" }}
          >
            <Swiper
              style={
                {
                  "--swiper-navigation-color": "#fff",
                  "--swiper-pagination-color": "#fff",
                } as React.CSSProperties
              }
              loop={true}
              spaceBetween={10}
              thumbs={{
                swiper:
                  thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
              }}
              navigation={{
                nextEl: ".gallery-custom-next",
                prevEl: ".gallery-custom-prev",
              }}
              modules={[FreeMode, Navigation, Thumbs, Keyboard, Pagination]}
              keyboard={{ enabled: true }}
              pagination={{ clickable: true }}
              initialSlide={activeIndex}
              onSlideChange={(swiper) => {
                setActiveIndex(swiper.realIndex);
                if (thumbsSwiper && !thumbsSwiper.destroyed) {
                  thumbsSwiper.slideToLoop(swiper.realIndex);
                }
              }}
              className="h-full"
            >
              {filteredImages?.map((img: any, index: number) => (
                <SwiperSlide
                  key={index}
                  className="flex items-center justify-center"
                >
                  <OptimizedImage
                    src={img?.GalleryImage}
                    alt={img?.ImgAlt || "Gallery Image"}
                    width={1200}
                    height={800}
                    className="object-contain m-auto"
                    style={{ width: "90%", height: "calc(100vh - 112px)" }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className={`gallery-navigation-btn`}>
              <div className="swiper-button-prev gallery-custom-prev">
                <ChevronLeftIcon className="text-white" />
              </div>
              <div className="swiper-button-next gallery-custom-next">
                <ChevronRightIcon className="text-white" />
              </div>
            </div>
          </div>

          <div className="px-5 pb-5">
            <Swiper
              onSwiper={setThumbsSwiper}
              loop={true}
              spaceBetween={12}
              slidesPerView="auto"
              freeMode={true}
              watchSlidesProgress={true}
              centeredSlides={true}
              slideToClickedSlide={true}
              modules={[FreeMode, Navigation, Thumbs]}
              className="h-24"
            >
              {filteredImages?.map((img: any, index: number) => (
                <SwiperSlide
                  key={index}
                  className="cursor-pointer rounded-lg overflow-hidden transition-all"
                  style={{
                    width: "96px",
                    opacity: index === activeIndex ? 1 : 0.4,
                    border: index === activeIndex ? "3px solid white" : "none",
                  }}
                >
                  <OptimizedImage
                    src={img?.GalleryImage}
                    alt={img?.ImgAlt || "Gallery Thumbnail"}
                    width={150}
                    height={150}
                    className="w-full h-full object-cover"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}

      <style>{`
        .gallery-navigation-btn{
          top: 50%;
          position: absolute;
          z-index: 1;
          transform: translate(0, -50%);
          width: 100%;
          display: flex;
          justify-content: space-between;
          pointer-events:none;
        }

        .gallery-custom-next,
        .gallery-custom-prev {
          width: 50px;
          height: 50px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          backdrop-filter: blur(10px);
          font-size:12px;
          display: flex;
          justify-content: center;
          align-items: center;
          pointer-events:all;
        }

        .gallery-custom-next svg,
        .gallery-custom-prev svg{
          width:24px;
        }

        .gallery-custom-next:after,
        .gallery-custom-prev:after {
          display: none;
        }

        .gallery-custom-next:hover,
        .gallery-custom-prev:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .swiper-pagination-bullet {
          background: white;
          opacity: 0.5;
        }

        .swiper-pagination-bullet-active {
          opacity: 1;
        }
      `}</style>
    </>
  );
}
